import type React from 'react';

import { FormHelperText, Stack, Typography } from '@mui/material';
import { useTenant } from 'probonio-shared-ui/module/me';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FlexBenefitOptions } from '../flex/extension/flex-benefit-activation-dialog.extension';
import { NonCashBenefit, NonCashBenefitOptions } from './const';
import { useNonCashBenefitConfig } from './hooks';
import { NonCashBenefitCard } from './NonCashOption';

type FormValue = NonCashBenefitOptions | FlexBenefitOptions;

export type SelectNonCashBenefitsFormProps = {
  /** Non-cash benefits that should be shown but cannot be changed. */
  enforcedNonCashBenefits?: readonly NonCashBenefit[];
};

export const SelectNonCashBenefitsForm: React.FC<SelectNonCashBenefitsFormProps> = ({ enforcedNonCashBenefits }) => {
  const { tenant } = useTenant();
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext<{ benefitOptions: FormValue }>();
  const { nonCashBenefits, monthlyBudget } = watch('benefitOptions');

  if (!enforcedNonCashBenefits) {
    enforcedNonCashBenefits = tenant?.nonCashBenefits?.length === 1 ? tenant?.nonCashBenefits : undefined;
  }

  const selectableNonCashBenefits = tenant?.nonCashBenefits ?? [];

  // the dropdown provides numbers, the number input provides strings
  const amount = typeof monthlyBudget === 'number' ? monthlyBudget : monthlyBudget && parseFloat(monthlyBudget);
  const { t } = useTranslation('benefitsModule');

  const { config, isLoading } = useNonCashBenefitConfig();

  register('benefitOptions.nonCashBenefits', {
    validate: value =>
      value && value.length > 0 && (!enforcedNonCashBenefits?.length || enforcedNonCashBenefits.every(feature => value.includes(feature))),
  });

  const validFeatures = useMemo(() => {
    const candidateFeatures = new Set([...(enforcedNonCashBenefits ?? []), ...(nonCashBenefits ?? [])]);
    return [...candidateFeatures].filter(feature => config[feature].isValidValue(amount));
  }, [enforcedNonCashBenefits, nonCashBenefits, amount, config]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if ((nonCashBenefits?.length ?? 0) !== (validFeatures?.length ?? 0)) {
      setValue('benefitOptions.nonCashBenefits', validFeatures, { shouldDirty: false });
    }
  }, [isLoading, nonCashBenefits?.length, setValue, validFeatures]);

  const handleFeatureToggle = (nonCashBenefit: NonCashBenefit, enabled: boolean) => {
    if (enforcedNonCashBenefits?.includes(nonCashBenefit)) {
      return;
    }
    const currentFeatures = nonCashBenefits ?? [];
    let newFeatures: NonCashBenefit[];

    if (enabled) {
      newFeatures = [...currentFeatures, nonCashBenefit];
    } else {
      newFeatures = currentFeatures.filter(f => f !== nonCashBenefit);
    }

    setValue('benefitOptions.nonCashBenefits', newFeatures, { shouldDirty: true, shouldTouch: true });
  };

  return selectableNonCashBenefits.length > 0 ? (
    <>
      <Typography variant="h3" fontWeight="medium" gutterBottom>
        {t('COUPONS.fields.nonCashBenefits')}
      </Typography>
      {errors.benefitOptions?.nonCashBenefits && (
        <FormHelperText sx={{ mb: 1 }} error={true}>
          {t('nonCashBenefits.activationDialog.error')}
        </FormHelperText>
      )}
      <Stack spacing={2}>
        {selectableNonCashBenefits.map(featureType => {
          return (
            <NonCashBenefitCard
              key={featureType}
              featureType={featureType}
              config={config[featureType]}
              amount={amount}
              isSelected={!!nonCashBenefits?.includes(featureType)}
              onToggle={handleFeatureToggle}
              disabled={enforcedNonCashBenefits?.includes(featureType)}
            />
          );
        })}
      </Stack>
    </>
  ) : null;
};
